import Navbar from "../components/NavbarM";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import {
  CreditCard,
  Keyboard,
  CheckCircle,
  User,
  Mail,
  Coins,
  ShieldCheck,
} from "lucide-react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import "./SwipeCard.css";

function SwipeCardPage() {
  const navigate = useNavigate();

  const [cardNumber, setCardNumber] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const lastFetchedCardRef = useRef("");

  const storeId = localStorage.getItem("storeId");
  const storeUid = localStorage.getItem("storeUid");
  const storeName = localStorage.getItem("storeName");

  const extractCardNumber = (raw) => {
    const value = String(raw || "").trim();

    if (value.includes("=")) {
      return value.split("=")[0].replace(/\D/g, "").trim();
    }

    const track2Match = value.match(/;?(\d{16})=/);
    if (track2Match) return track2Match[1];

    const track1Match = value.match(/B?(\d{16})\^/);
    if (track1Match) return track1Match[1];

    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length >= 16) {
      return digitsOnly.slice(0, 16);
    }

    return digitsOnly;
  };

  const validateCard = async (cleanedCardNumber) => {
    const cardRef = doc(db, "cards", cleanedCardNumber);
    const cardSnap = await getDoc(cardRef);

    if (!cardSnap.exists()) {
      throw new Error("This is not a valid PointsHub card.");
    }

    const cardData = cardSnap.data();

    if (cardData.status === "blocked" || cardData.status === "lost") {
      throw new Error("This card is blocked or lost. Please contact admin.");
    }

    if (cardData.activated !== true && cardData.status !== "activated") {
      throw new Error(
        "This card is not activated yet. Please add member first.",
      );
    }

    return cardData;
  };

  const fetchOrCreateStoreMember = async (rawCard) => {
    const cleaned = extractCardNumber(rawCard);

    console.log("Raw card input:", rawCard);
    console.log("Clean Firebase card:", cleaned);

    setMemberData(null);

    if (!cleaned || cleaned.length < 16) return;

    if (!storeId || !storeUid) {
      alert("❌ Store not logged in. Please sign in again.");
      return;
    }

    try {
      setLoading(true);

      // 1. Card must already exist in admin-created cards collection.
      // 2. Card must not be blocked/lost.
      // 3. Card must be activated before normal swipe use.
      await validateCard(cleaned);

      const storeMemberId = `${storeId}_${cleaned}`;
      const storeMemberRef = doc(db, "storeMembers", storeMemberId);
      const storeMemberSnap = await getDoc(storeMemberRef);

      if (storeMemberSnap.exists()) {
        const data = storeMemberSnap.data();

        setMemberData({
          ...data,
          cardNumber: cleaned,
        });

        return;
      }

      // Card is valid and activated, but this store has not used it before.
      // So create this store's own balance.
      const newMember = {
        storeId,
        storeUid,
        storeName: storeName || "",
        cardNumber: cleaned,
        name: "Guest",
        email: "",
        points: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        autoCreated: true,
      };

      await setDoc(storeMemberRef, newMember);

      setMemberData({
        ...newMember,
        cardNumber: cleaned,
      });
    } catch (err) {
      console.error("🔥 Swipe fetch error:", err);
      setMemberData(null);
      alert(
        `❌ ${err.message || "Something went wrong while fetching card info."}`,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cleaned = extractCardNumber(cardNumber);

    if (!cleaned || cleaned.length < 16) return;
    if (cleaned === lastFetchedCardRef.current) return;

    const timer = setTimeout(() => {
      lastFetchedCardRef.current = cleaned;
      fetchOrCreateStoreMember(cardNumber);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [cardNumber]);

  useEffect(() => {
    let buffer = "";
    let reading = false;

    const handleKeyDown = (e) => {
      const key = e.key;

      if (key === ";") {
        reading = true;
        buffer = key;
        return;
      }

      if (reading) {
        buffer += key;

        if (key === "?") {
          reading = false;

          const displayValue = buffer.replace(/^;/, "").replace(/\?$/, "");

          setCardNumber(displayValue);
          fetchOrCreateStoreMember(displayValue);

          buffer = "";
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line
  }, []);

  const handleManualEntry = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleConfirm = () => {
    const cleaned = extractCardNumber(cardNumber);

    if (!cleaned || !memberData) {
      alert("Please swipe a valid activated PointsHub card first.");
      return;
    }

    navigate("/PointsActionPage", {
      replace: true,
      state: { cardNumber: cleaned, memberData },
    });
  };

  const displayCard =
    cardNumber.length > 28 ? `${cardNumber.slice(0, 22)}...` : cardNumber;

  return (
    <>
      <Navbar />

      <div className="swipe-page">
        <div className="swipe-container">
          <section className="swipe-header">
            <div>
              <div className="swipe-pill">
                <ShieldCheck size={18} />
                Secure Card Terminal
              </div>
              <h1>Swipe Customer Card</h1>
              <p>
                Swipe the points card or enter the card number manually to
                continue.
              </p>
            </div>

            <div className="swipe-store-card">
              <span>Active Store</span>
              <strong>{storeName || "Store"}</strong>
            </div>
          </section>

          <div className="swipe-grid">
            <Card className="swipe-card-panel">
              <div className="scanner-visual">
                <div className="scanner-ring">
                  <CreditCard size={50} />
                </div>
                <div className="scanner-line"></div>
              </div>

              <h3>Ready for Swipe</h3>
              <p className="text-muted">
                Card reader input will appear below automatically.
              </p>

              <input
                type="text"
                ref={inputRef}
                className="swipe-input"
                placeholder="Swipe or enter card number..."
                value={cardNumber}
                onChange={(e) => {
                  setCardNumber(e.target.value);
                  setMemberData(null);
                  lastFetchedCardRef.current = "";
                }}
              />

              {cardNumber && (
                <div className="raw-card-preview">
                  <span>Input detected</span>
                  <p>{displayCard}</p>
                </div>
              )}

              <div className="swipe-actions">
                <Button
                  className="secondary-action-btn"
                  onClick={handleManualEntry}
                >
                  <Keyboard size={18} />
                  Enter Manually
                </Button>

                <Button
                  className="secondary-action-btn"
                  onClick={() => fetchOrCreateStoreMember(cardNumber)}
                  disabled={!cardNumber || loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} />
                      Check Card
                    </>
                  )}
                </Button>
              </div>
            </Card>

            <Card className="member-card-panel">
              {!memberData ? (
                <div className="empty-member-state">
                  <div className="empty-icon">
                    <User size={42} />
                  </div>
                  <h3>No Card Loaded</h3>
                  <p>
                    Swipe a valid activated PointsHub card to view member
                    details and continue to points actions.
                  </p>
                </div>
              ) : (
                <>
                  <div className="member-success">
                    <CheckCircle size={22} />
                    Card Verified
                  </div>

                  <div className="member-details">
                    <div className="detail-row">
                      <div className="detail-icon">
                        <User size={20} />
                      </div>
                      <div>
                        <span>Member Name</span>
                        <strong>{memberData.name || "Guest"}</strong>
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-icon">
                        <Mail size={20} />
                      </div>
                      <div>
                        <span>Email</span>
                        <strong>{memberData.email || "Not Provided"}</strong>
                      </div>
                    </div>

                    <div className="detail-row highlight">
                      <div className="detail-icon coins">
                        <Coins size={20} />
                      </div>
                      <div>
                        <span>Current Balance</span>
                        <strong>{memberData.points} pts</strong>
                      </div>
                    </div>
                  </div>

                  {memberData.autoCreated && (
                    <div className="auto-created-note">
                      This card is valid. A fresh balance has started for this
                      store.
                    </div>
                  )}

                  <Button
                    className="confirm-action-btn"
                    onClick={handleConfirm}
                    disabled={!memberData || loading}
                  >
                    Confirm Points
                  </Button>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export default SwipeCardPage;
